import React, { useMemo, useState } from "react";

import { Eyebrow, cx, SearchIcon, Title } from "../components/CommonComponents";

function CurriculumTable({ semester, dark = false }) {
  if (!semester?.subjects?.length) {
    return (
      <div
        className={cx(
          "rounded-xl border p-8 text-center text-sm",
          dark
            ? "border-white/10 text-white/60"
            : "border-[#071A2F]/10 text-[#65717D]"
        )}
      >
        No subjects available for this semester.
      </div>
    );
  }

  const headers = [
    "Subject",
    "Code",
    "Credits",
    "CT",
    "TA",
    "ESE",
    "Practical",
    "Total",
  ];

  return (
    <div
      className={cx(
        "mt-5 overflow-x-auto border",
        dark ? "border-white/15" : "border-[#071A2F]/10"
      )}
    >
      <table className="w-full text-left text-sm">
        <thead className={dark ? "bg-white/10" : "bg-[#071A2F] text-white"}>
          <tr>
            {headers.map((header) => (
              <th key={header} className="p-4 font-bold whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {semester.subjects.map((subject, index) => (
            <tr
              key={`${subject.code || subject.title}-${index}`}
              className={cx(
                "border-t",
                dark ? "border-white/10" : "border-[#071A2F]/10"
              )}
            >
              <td
                className={cx(
                  "p-4 font-semibold",
                  dark ? "text-white" : "text-[#071A2F]"
                )}
              >
                {subject.title}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.code}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.credits}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.ct}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.ta}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.ese}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.practical}
              </td>

              <td className="p-4 text-[#65717D]">
                {subject.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Subjects({ curriculum }) {
  const [activeSemester, setActiveSemester] = useState(0);
  const [subjectSearch, setSubjectSearch] = useState("");

  const currentSemester = curriculum[activeSemester] ?? curriculum[0] ?? null;

  const filteredSubjects = useMemo(() => {
    if (!currentSemester?.subjects) {
      return [];
    }

    const term = subjectSearch.trim().toLowerCase();

    if (!term) {
      return currentSemester.subjects;
    }

    return currentSemester.subjects.filter((subject) =>
      subject.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [currentSemester, subjectSearch]);

  if (!curriculum) {
    return null;
  }

  if (!curriculum.length) {
    return (
      <section className="bg-[#F7F2E8] relative scroll-mt-6 bg-[#EEF4F8] py-20 sm:py-24 lg:py-28">
        <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <div className="rounded-[28px] border border-[#071A2F]/10 bg-white p-8 text-center">
            <p className="text-sm text-[#65717D]">
              Curriculum information is not available for this course.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="syllabus" className="relative scroll-mt-6 bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">
      <div className="mx-auto w-[min(1320px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
        <header className="grid gap-8 border-b border-[#071A2F]/15 pb-10 lg:grid-cols-[1.2fr_.8fr] lg:items-end">
          <div>
            <Eyebrow>Curriculum Structure</Eyebrow>

            <Title highlight="journey semester by semester" position="end">
              Explore the academic
            </Title>
          </div>

          <p className="text-base leading-8 text-[#65717D]">
            Replace the sample subjects with the approved syllabus for the
            selected programme.
          </p>
        </header>

        <div className="mt-10 grid min-w-0 gap-6 md:gap-8 xl:grid-cols-[250px_minmax(0,1fr)]">
          {/* Semester navigation */}
          <aside className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-1 content-start">
            {curriculum.map((semester, index) => (
              <button
                key={`${semester.title}-${index}`}
                type="button"
                onClick={() => {
                  setActiveSemester(index);
                  setSubjectSearch("");
                }}
                className={cx(
                  "flex min-h-[62px] w-full items-center justify-between rounded-2xl px-4 sm:px-5 text-left text-sm font-bold transition",
                  activeSemester === index
                    ? "bg-[#071A2F] text-white"
                    : "border border-[#071A2F]/10 bg-white text-[#071A2F] hover:border-[#D5AD57]",
                )}
              >
                <span className="truncate">{semester.title}</span>

                <span
                  className={
                    activeSemester === index
                      ? "ml-3 shrink-0 text-[#D5AD57]"
                      : "ml-3 shrink-0 text-[#8B1E3F]"
                  }
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </aside>

          {/* Curriculum content */}
          <div className="min-w-0 overflow-hidden rounded-[28px] border border-[#071A2F]/10 bg-white p-5 sm:p-8">
            <div className="flex flex-col gap-5 border-b border-[#071A2F]/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#8B1E3F]">
                  Selected Semester
                </span>

                <h3 className="mt-2 truncate font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F]">
                  {currentSemester?.title}
                </h3>
              </div>

              <label className="relative block w-full sm:w-[280px] sm:shrink-0">
                <span className="sr-only">Search subjects</span>

                <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#071A2F]/35" />

                <input
                  type="search"
                  value={subjectSearch}
                  onChange={(event) => setSubjectSearch(event.target.value)}
                  placeholder="Search subjects..."
                  className="h-12 w-full rounded-full border border-[#071A2F]/10 bg-[#F7F2E8] pl-11 pr-4 text-sm text-[#071A2F] outline-none transition placeholder:text-[#65717D]/60 focus:border-[#8B1E3F]"
                />
              </label>
            </div>

            {/* Curriculum table */}
            <div className="mt-6 min-w-0 overflow-x-auto">
              <CurriculumTable
                semester={{
                  ...currentSemester,
                  subjects: filteredSubjects,
                }}
              />
            </div>

            {subjectSearch && filteredSubjects.length === 0 && (
              <p className="mt-5 text-center text-sm text-[#65717D]">
                No subjects found for "{subjectSearch}".
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
